import { MinusCircleOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ApprovalFlowRow } from '@hq/api-sdk';
import { ApiError } from '@hq/api-sdk';
import { hasPermission } from '@hq/permission';
import {
  APPROVAL_FLOW_STATUS_META,
  APPROVAL_NODE_TYPE_OPTIONS,
  APPROVAL_SCOPE_OPTIONS,
  approvalFlowStatusMeta,
  approvalNodeTypeLabel,
  approvalScopeLabel,
  formatDisplayTime
} from '@hq/utils';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  App,
  Button,
  Card,
  Drawer,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Typography
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useEffect, useState } from 'react';
import { hq } from '../api/client';
import { sumWidth } from '@hq/ui-kit';
import { LEVEL_LABELS } from '../config';
import { useAuthStore } from '../stores/auth';

// 状态 / 节点类型 / 审批人来源统一取自 `@hq/utils`（另一端 ops 的审批流配置页共用同一份，
// 曾各写一份导致 DRAFT 在两端配色不一致）。

const BIZ_TYPES = [
  { value: 'APPLY', label: '科室申领' },
  { value: 'REQUIRE', label: '请购' },
  { value: 'PURCHASE', label: '采购订单' },
  { value: 'SETTLE', label: '结算' },
  { value: 'BUDGET', label: '预算' },
  { value: 'PRICE_CHANGE', label: '调价' }
];

/** 节点类型必须与后端 `ApprovalEngine.NodeTypes` 完全一致；曾用 SIGN 导致会签静默退化为单签。 */
const NODE_TYPES = APPROVAL_NODE_TYPE_OPTIONS;

/** 审批人来源必须是后端枚举：DEPT_MANAGER / ORG_LEADER 会落入 default 分支并可能解析不出审批人。 */
const SCOPES = APPROVAL_SCOPE_OPTIONS;

const COUNTER_SIGN_ORDERS = [
  { value: 'SERIAL', label: '串行' },
  { value: 'PARALLEL', label: '并行' }
];

const TIMEOUT_ACTIONS = [
  { value: 'REMIND', label: '催办提醒' },
  { value: 'ESCALATE', label: '上抛升级' },
  { value: 'TRANSFER_ADMIN', label: '转办管理员' },
  { value: 'AUTO_PASS', label: '自动通过' },
  { value: 'AUTO_REJECT', label: '自动驳回' }
];

/** 账号等级（sys_account_level.level_code）：手填拼错会让审批人解析/上抛静默失效。 */
const LEVEL_OPTIONS = Object.entries(LEVEL_LABELS).map(([code, text]) => ({ value: code, label: text }));

/** 条件 JSON 前端先校验，避免等到后端 EnsureJson 才报 500/422。 */
const jsonRule = (label: string) => ({
  validator: (_rule: unknown, value?: string) => {
    const text = (value ?? '').trim();
    if (!text) return Promise.resolve();
    try {
      JSON.parse(text);
      return Promise.resolve();
    } catch {
      return Promise.reject(new Error(`${label}不是合法 JSON`));
    }
  }
});

/** 指定人员多选：手填 userId 猜不到是谁，写错只会让节点解析出一个不存在的审批人。 */
function UserMultiPicker({
  value,
  onChange,
  nameMap
}: {
  value?: string[];
  onChange?: (value?: string[]) => void;
  nameMap?: Record<string, string>;
}) {
  const [keyword, setKeyword] = useState('');
  const users = useQuery({
    queryKey: ['approval', 'user-search', keyword],
    staleTime: 30_000,
    queryFn: () => hq.approval.searchUsers(keyword || undefined, 50)
  });
  const options = new Map<string, string>(
    (users.data ?? []).map((row) => [row.id, `${row.name ?? '未命名'}（${row.userCode ?? row.id}）`] as const)
  );
  (value ?? []).forEach((id) => {
    const known = nameMap?.[id];
    if (!options.has(id)) options.set(id, known ? `${known}（${id}）` : id);
  });
  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      optionFilterProp="label"
      maxTagCount="responsive"
      placeholder="搜索姓名 / 工号"
      value={value}
      onSearch={setKeyword}
      onChange={(next) => onChange?.(next)}
      loading={users.isFetching}
      options={[...options].map(([id, label]) => ({ value: id, label }))}
    />
  );
}

/** 审批流配置（医院端）：流程/节点/条件路由/独立授权，发布后对发起人即时生效。 */
export default function SystemFlow() {
  const { message, modal } = App.useApp();
  const queryClient = useQueryClient();
  const profile = useAuthStore((state) => state.profile);
  const canManage = profile?.level === 'admin' || (profile?.perms ? hasPermission(profile.perms, 'sys:approval:manage') : false);

  const [status, setStatus] = useState<string>('');
  const [bizType, setBizType] = useState<string>('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailTab, setDetailTab] = useState('node');
  const [editing, setEditing] = useState<ApprovalFlowRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [form] = Form.useForm();
  /** 编辑时把详情里的 userId → userName 暂存，给人选下拉做回显。 */
  const [userNames, setUserNames] = useState<Record<string, string>>({});
  /** 条件路由的目标节点只能从本流程已配置的节点里选。 */
  const nodeValues = Form.useWatch('nodes', form) as { nodeCode?: string; nodeName?: string }[] | undefined;

  const roles = useQuery({
    queryKey: ['basic', 'roles'],
    queryFn: () => hq.admin.roles.list(),
    enabled: canManage
  });

  const nodeOptions = (nodeValues ?? [])
    .filter((node) => Boolean(node?.nodeCode))
    .map((node) => ({ value: String(node.nodeCode), label: `${node.nodeName || node.nodeCode}（${node.nodeCode}）` }));

  const flows = useQuery({
    queryKey: ['system', 'flow', status, bizType],
    queryFn: () => hq.approvalFlow.flow.list({ status: status || undefined, bizType: bizType || undefined })
  });

  const detail = useQuery({
    queryKey: ['system', 'flow', 'detail', detailId],
    enabled: Boolean(detailId),
    queryFn: () => hq.approvalFlow.flow.detail(detailId ?? '')
  });

  const auths = useQuery({
    queryKey: ['system', 'flow', 'auth', detailId],
    enabled: Boolean(detailId),
    queryFn: () => hq.approvalFlow.auth.list(detailId ?? '')
  });

  useEffect(() => {
    if (!detail.data || !editing) return;
    // 编辑时用最新详情回填，避免列表行缺少节点
  }, [detail.data, editing]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: ['system', 'flow'] });
  };

  const save = useMutation({
    mutationFn: async () => {
      const values = await form.validateFields();
      const body = {
        flowCode: values.flowCode,
        flowName: values.flowName,
        bizType: values.bizType,
        applicableScope: values.applicableScope,
        isEmergencyFlow: values.isEmergencyFlow,
        allowTransfer: values.allowTransfer,
        allowAddSigner: values.allowAddSigner,
        allowRejectTo: values.allowRejectTo,
        supportBatchApprove: values.supportBatchApprove,
        remark: values.remark,
        startConditionJson: values.startConditionJson?.trim() || undefined,
        nodes: (values.nodes ?? []).map((node: Record<string, unknown>, index: number) => {
          const userIds = (node.userIds as string[] | undefined) ?? [];
          return {
            nodeCode: node.nodeCode,
            nodeName: node.nodeName,
            nodeType: node.nodeType,
            seq: Number(node.seq) > 0 ? Number(node.seq) : index + 1,
            approveScope: node.approveScope,
            // 表单是多选数组，提交时还原成后端的逗号分隔串
            roleCode: Array.isArray(node.roleCode)
              ? node.roleCode.map((code) => String(code).trim()).filter(Boolean).join(',') || undefined
              : (node.roleCode as string) || undefined,
            levelRequired: node.levelRequired || undefined,
            signRate: node.signRate ?? 100,
            allowDelegate: true,
            counterSignOrder: (node.counterSignOrder as string | undefined) ?? 'SERIAL',
            timeoutHours: node.timeoutHours ?? 0,
            timeoutAction: (node.timeoutAction as string | undefined) ?? 'REMIND',
            escalateLevelCode: node.escalateLevelCode || undefined,
            users: userIds.map((userId) => ({ userId }))
          };
        }),
        routes: (values.routes ?? []).filter((route: Record<string, unknown>) => route.targetNodeCode)
      };
      return editing ? hq.approvalFlow.flow.update(editing.id, body) : hq.approvalFlow.flow.create(body);
    },
    onSuccess: async () => {
      message.success('审批流已保存（发布后生效）');
      setFormOpen(false);
      await invalidate();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '保存失败')
  });

  const publish = useMutation({
    mutationFn: (id: string) => hq.approvalFlow.flow.publish(id),
    onSuccess: async () => {
      message.success('已发布');
      await invalidate();
      await detail.refetch();
    },
    onError: (error) => message.error(error instanceof ApiError ? error.friendlyMessage : '发布失败')
  });

  const disable = (row: ApprovalFlowRow) =>
    modal.confirm({
      title: `停用 ${row.flowName}`,
      content: '停用后将回落到上级/平台模板；已发起单据不受影响。',
      okButtonProps: { danger: true },
      onOk: async () => {
        await hq.approvalFlow.flow.disable(row.id);
        message.success('已停用');
        await invalidate();
      }
    });

  const openEditor = async (row: ApprovalFlowRow | null) => {
    setEditing(row);
    form.resetFields();
    if (row) {
      const data = await hq.approvalFlow.flow.detail(row.id);
      const names: Record<string, string> = {};
      data.nodes.forEach((node) => {
        node.users.forEach((item) => {
          if (item.userName) names[item.userId] = item.userName;
        });
      });
      setUserNames(names);
      form.setFieldsValue({
        ...data.flow,
        startConditionJson: data.flow.startConditionJson ?? undefined,
        nodes: data.nodes.map((node) => ({
          nodeCode: node.nodeCode,
          nodeName: node.nodeName,
          nodeType: node.nodeType,
          seq: node.seq,
          approveScope: node.approveScope,
          roleCode: node.roleCode ? node.roleCode.split(',').map((code) => code.trim()).filter(Boolean) : undefined,
          levelRequired: node.levelRequired ?? undefined,
          timeoutHours: node.timeoutHours,
          signRate: node.signRate,
          counterSignOrder: node.counterSignOrder,
          timeoutAction: node.timeoutAction ?? 'REMIND',
          escalateLevelCode: node.escalateLevelCode ?? undefined,
          userIds: node.users.filter((item) => item.userId !== '0').map((item) => item.userId)
        })),
        routes: data.routes.map((route) => ({
          targetNodeCode: route.targetNodeCode,
          priority: route.priority,
          conditionJson: route.conditionJson,
          description: route.description
        }))
      });
    } else {
      setUserNames({});
      form.setFieldsValue({
        bizType: 'APPLY',
        applicableScope: 'ORG',
        allowTransfer: true,
        allowAddSigner: false,
        allowRejectTo: true,
        supportBatchApprove: true,
        nodes: [
          {
            nodeCode: 'DEPT_APPROVE',
            nodeName: '科室审批',
            nodeType: 'APPROVE',
            seq: 10,
            approveScope: 'DEPT_HEAD',
            timeoutHours: 24,
            timeoutAction: 'REMIND',
            counterSignOrder: 'SERIAL',
            signRate: 100
          }
        ]
      });
    }
    setFormOpen(true);
  };

  const columns: ColumnsType<ApprovalFlowRow> = [
    { title: '流程编码', dataIndex: 'flowCode', width: 180 },
    { title: '名称', dataIndex: 'flowName', width: 220 },
    { title: '业务', dataIndex: 'bizType', width: 130, render: (value: string) => <Tag>{BIZ_TYPES.find((item) => item.value === value)?.label ?? value}</Tag> },
    { title: '机构', dataIndex: 'orgId', width: 100, render: (value: string) => (value === '0' ? <Tag color="blue">平台</Tag> : value) },
    { title: '版本', dataIndex: 'version', width: 80, align: 'right' },
    { title: '应急', dataIndex: 'isEmergencyFlow', width: 80, render: (value: boolean) => (value ? <Tag color="red">应急</Tag> : '-') },
    {
      title: '状态',
      dataIndex: 'status',
      width: 100,
      render: (value: string) => {
        const meta = approvalFlowStatusMeta(value);
        return <Tag color={meta.color}>{meta.text}</Tag>;
      }
    },
    { title: '更新时间', dataIndex: 'updateAt', width: 160, render: (value: string | null) => formatDisplayTime(value) },
    {
      title: '操作',
      width: 240,
      fixed: 'right',
      render: (_, row) => (
        <Space size={0}>
          <Button type="link" onClick={() => setDetailId(row.id)}>
            详情
          </Button>
          <Button type="link" disabled={!canManage} onClick={() => openEditor(row)}>
            编辑
          </Button>
          {row.status !== 'PUBLISHED' ? (
            <Button type="link" disabled={!canManage} loading={publish.isPending} onClick={() => publish.mutate(row.id)}>
              发布
            </Button>
          ) : (
            <Button type="link" danger disabled={!canManage} onClick={() => disable(row)}>
              停用
            </Button>
          )}
        </Space>
      )
    }
  ];

  return (
    <Card
      className="page-card"
      title={<Space>审批流配置<Typography.Text type="secondary">流程 / 节点 / 路由 / 授权（编辑需 sys:approval:manage）</Typography.Text></Space>}
      extra={
        <Space>
          <Select
            allowClear
            placeholder="业务类型"
            style={{ width: 150 }}
            value={bizType || undefined}
            onChange={(value) => setBizType(value ?? '')}
            options={BIZ_TYPES}
          />
          <Select
            allowClear
            placeholder="状态"
            style={{ width: 120 }}
            value={status || undefined}
            onChange={(value) => setStatus(value ?? '')}
            options={Object.entries(APPROVAL_FLOW_STATUS_META).map(([value, meta]) => ({ value, label: meta.text }))}
          />
          <Button icon={<ReloadOutlined />} onClick={() => invalidate()} loading={flows.isFetching}>
            刷新
          </Button>
          <Button type="primary" icon={<PlusOutlined />} disabled={!canManage} onClick={() => openEditor(null)}>
            新建流程
          </Button>
        </Space>
      }
    >
      {!canManage ? <Alert type="info" showIcon style={{ marginBottom: 12 }} message="当前账号只读：审批流维护需要 sys:approval:manage。" /> : null}
      {flows.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          message="审批流加载失败"
          description={flows.error instanceof ApiError ? flows.error.friendlyMessage : String(flows.error)}
        />
      ) : null}
      <Table<ApprovalFlowRow>
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={flows.data ?? []}
        loading={flows.isPending}
        pagination={false}
        scroll={{ x: sumWidth(columns) }}
      />

      <Drawer title={detail.data?.flow.flowName ?? '流程详情'} width={860} open={Boolean(detailId)} onClose={() => setDetailId(null)}>
        {detail.isPending ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : detail.isError ? (
          <Alert type="error" showIcon message="详情加载失败" description={detail.error instanceof ApiError ? detail.error.friendlyMessage : ''} />
        ) : detail.data ? (
          <Tabs
            activeKey={detailTab}
            onChange={setDetailTab}
            items={[
              {
                key: 'node',
                label: `节点（${detail.data.nodes.length}）`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={detail.data.nodes}
                    columns={[
                      { title: '序', dataIndex: 'seq', width: 60 },
                      { title: '节点', dataIndex: 'nodeName', width: 160, render: (v: string, row) => `${v}（${row.nodeCode}）` },
                      // 详情同样按中文展示：英文原码只有配置人员看得懂，业务方核对时常误判
                      { title: '类型', dataIndex: 'nodeType', width: 110, render: (value: string) => approvalNodeTypeLabel(value) },
                      { title: '审批范围', dataIndex: 'approveScope', width: 150, render: (value: string) => approvalScopeLabel(value) },
                      { title: '角色/等级', width: 160, render: (_v, row) => row.roleCode || row.levelRequired || '-' },
                      { title: '会签比例', dataIndex: 'signRate', width: 100, render: (v: number) => `${v}%` },
                      { title: '超时(h)', dataIndex: 'timeoutHours', width: 90, align: 'right' }
                    ]}
                  />
                )
              },
              {
                key: 'route',
                label: `条件路由（${detail.data.routes.length}）`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    dataSource={detail.data.routes}
                    columns={[
                      { title: '优先级', dataIndex: 'priority', width: 80 },
                      { title: '目标节点', dataIndex: 'targetNodeCode', width: 160 },
                      { title: '条件', dataIndex: 'conditionJson', render: (v: string | null) => <Typography.Text code>{v || '默认'}</Typography.Text> },
                      { title: '说明', dataIndex: 'description', width: 220, render: (v: string | null) => v || '-' }
                    ]}
                  />
                )
              },
              {
                key: 'auth',
                label: `授权（${auths.data?.length ?? 0}）`,
                children: (
                  <Table
                    rowKey="id"
                    size="small"
                    pagination={false}
                    loading={Boolean(detailId) && auths.isPending}
                    dataSource={auths.data ?? []}
                    locale={{ emptyText: '未配置独立授权（默认按角色权限放行）' }}
                    columns={[
                      { title: '授权类型', dataIndex: 'authType', width: 130 },
                      { title: '对象类型', dataIndex: 'targetType', width: 110 },
                      { title: '对象', dataIndex: 'targetCodes', render: (v: string) => v || '-' },
                      { title: '状态', dataIndex: 'status', width: 90, render: (v: string) => <Tag color={v === 'ACTIVE' ? 'green' : 'default'}>{v}</Tag> },
                      { title: '生效至', dataIndex: 'effectiveTo', width: 140, render: (v: string | null) => (v ? String(v).slice(0, 10) : '-') }
                    ]}
                  />
                )
              }
            ]}
          />
        ) : null}
      </Drawer>

      <Modal
        title={editing ? `编辑审批流 · ${editing.flowName}` : '新建审批流'}
        open={formOpen}
        onCancel={() => setFormOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={save.isPending}
        width={900}
        destroyOnHidden
      >
        <Form form={form} layout="vertical" onFinish={() => save.mutate()} preserve={false}>
          <Space size={16} style={{ width: '100%' }}>
            <Form.Item name="flowCode" label="流程编码" rules={[{ required: true }]} style={{ width: 240 }}>
              <Input disabled={Boolean(editing)} maxLength={64} />
            </Form.Item>
            <Form.Item name="flowName" label="流程名称" rules={[{ required: true }]} style={{ width: 260 }}>
              <Input maxLength={128} />
            </Form.Item>
            <Form.Item name="bizType" label="业务类型" rules={[{ required: true }]} style={{ width: 180 }}>
              <Select options={BIZ_TYPES} />
            </Form.Item>
          </Space>
          <Space size={24}>
            <Form.Item name="isEmergencyFlow" label="应急流程" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="allowTransfer" label="允许转办" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="allowAddSigner" label="允许加签" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="allowRejectTo" label="允许退回" valuePropName="checked"><Switch /></Form.Item>
            <Form.Item name="supportBatchApprove" label="批量审批" valuePropName="checked"><Switch /></Form.Item>
          </Space>

          <Typography.Title level={5}>审批节点</Typography.Title>
          <Form.List name="nodes">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Card key={field.key} size="small" title={`节点 ${field.name + 1}`} extra={<Button type="link" danger onClick={() => remove(field.name)}>删除</Button>}>
                    <Space size={12} wrap>
                      <Form.Item name={[field.name, 'nodeCode']} label="编码" rules={[{ required: true, message: '必填' }]} style={{ width: 120 }}>
                        <Input placeholder="DEPT_APPROVE" />
                      </Form.Item>
                      <Form.Item name={[field.name, 'nodeName']} label="名称" rules={[{ required: true, message: '必填' }]} style={{ width: 160 }}>
                        <Input placeholder="科室审批" />
                      </Form.Item>
                      <Form.Item name={[field.name, 'nodeType']} label="类型" style={{ width: 130 }}>
                        <Select options={NODE_TYPES} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'seq']} label="顺序" style={{ width: 90 }}>
                        <InputNumber min={1} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'approveScope']} label="审批人来源" style={{ width: 160 }}>
                        <Select options={SCOPES} />
                      </Form.Item>
                      <Form.Item
                        name={[field.name, 'roleCode']}
                        label="角色"
                        style={{ width: 220 }}
                        dependencies={[['nodes', field.name, 'approveScope']]}
                        rules={[
                          ({ getFieldValue }) => ({
                            validator: () => {
                              const scope = getFieldValue(['nodes', field.name, 'approveScope']);
                              const codes = getFieldValue(['nodes', field.name, 'roleCode']) as string[] | undefined;
                              // 只能是下拉里的真实角色：手填拼错的码后端解析不到人，节点会静默无人可批
                              if (scope === 'ROLE' && !codes?.length)
                                return Promise.reject(new Error('按角色审批必须选择角色'));
                              return Promise.resolve();
                            }
                          })
                        ]}
                      >
                        <Select
                          mode="multiple"
                          allowClear
                          showSearch
                          optionFilterProp="label"
                          placeholder="选择角色（可多选）"
                          options={(roles.data ?? []).map((role) => ({ value: role.roleCode ?? role.id, label: `${role.roleName ?? role.roleCode}` }))}
                        />
                      </Form.Item>
                      <Form.Item name={[field.name, 'levelRequired']} label="账号等级" style={{ width: 160 }}>
                        <Select allowClear showSearch optionFilterProp="label" placeholder="不限等级" options={LEVEL_OPTIONS} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'userIds']} label="指定人员" style={{ width: 320 }}>
                        <UserMultiPicker nameMap={userNames} />
                      </Form.Item>
                    </Space>
                    <Space size={12} wrap>
                      <Form.Item
                        name={[field.name, 'signRate']}
                        label="会签比例(%)"
                        tooltip="0 = 全员会签"
                        style={{ width: 120 }}
                      >
                        <InputNumber min={0} max={100} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'counterSignOrder']} label="会签顺序" style={{ width: 130 }}>
                        <Select options={COUNTER_SIGN_ORDERS} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'timeoutHours']} label="超时(小时)" style={{ width: 110 }}>
                        <InputNumber min={0} max={720} style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'timeoutAction']} label="超时动作" style={{ width: 150 }}>
                        <Select options={TIMEOUT_ACTIONS} />
                      </Form.Item>
                      <Form.Item name={[field.name, 'escalateLevelCode']} label="上抛等级" style={{ width: 160 }}>
                        <Select allowClear showSearch optionFilterProp="label" placeholder="不上抛" options={LEVEL_OPTIONS} />
                      </Form.Item>
                    </Space>
                  </Card>
                ))}
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={() =>
                    add({
                      nodeType: 'APPROVE',
                      seq: (fields.length + 1) * 10,
                      approveScope: 'DEPT_HEAD',
                      signRate: 100,
                      counterSignOrder: 'SERIAL',
                      timeoutHours: 24,
                      timeoutAction: 'REMIND'
                    })
                  }
                  block
                >
                  添加节点
                </Button>
              </Space>
            )}
          </Form.List>

          <Typography.Title level={5} style={{ marginTop: 16 }}>条件路由（可选）</Typography.Title>
          <Form.List name="routes">
            {(fields, { add, remove }) => (
              <Space direction="vertical" style={{ width: '100%' }}>
                {fields.map((field) => (
                  <Space key={field.key} align="baseline" wrap>
                    <Form.Item
                      name={[field.name, 'targetNodeCode']}
                      label="目标节点"
                      rules={[{ required: true, message: '必选' }]}
                      style={{ width: 220 }}
                    >
                      <Select showSearch optionFilterProp="label" placeholder="选择本流程节点" options={nodeOptions} />
                    </Form.Item>
                    <Form.Item name={[field.name, 'priority']} label="优先级" initialValue={10} style={{ width: 100 }}>
                      <InputNumber min={0} placeholder="优先级" style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'conditionJson']}
                      label="条件 JSON"
                      style={{ width: 340 }}
                      rules={[jsonRule('条件路由表达式')]}
                    >
                      <Input placeholder='如 {"amount":{"op":"gte","value":10000}}' />
                    </Form.Item>
                    <Form.Item name={[field.name, 'description']} label="说明" style={{ width: 180 }}>
                      <Input placeholder="说明" />
                    </Form.Item>
                    <MinusCircleOutlined onClick={() => remove(field.name)} />
                  </Space>
                ))}
                <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ priority: 10 })} block>
                  添加路由
                </Button>
              </Space>
            )}
          </Form.List>
          <Form.Item name="startConditionJson" label="发起条件（JSON，可空）" style={{ marginTop: 16 }} rules={[jsonRule('发起条件')]}>
            <Input.TextArea rows={2} placeholder='{"amount":{"op":"gte","value":0}}' />
          </Form.Item>
          <Form.Item name="remark" label="备注" style={{ marginTop: 16 }}>
            <Input.TextArea rows={2} maxLength={255} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
}
